import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { BellRing, Building2, ExternalLink } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import api from '@/lib/api'

interface CrossOrgNotification {
  id: string
  organizationId: string
  organizationName: string
  type: string
  title: string
  body?: string
  data?: Record<string, any>
  priority: string
  createdAt: string
}

export default function CrossOrgNotifications() {
  const { selectOrganization } = useAuth()
  const [notifications, setNotifications] = useState<CrossOrgNotification[]>([])
  const [unread, setUnread] = useState(0)
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)

  const loadNotifications = useCallback(async () => {
    try {
      const res = await api.get('/api/notifications/cross-org')
      if (res.data.success) {
        setNotifications(res.data.notifications)
        setUnread(res.data.unread)
      }
    } catch {
      // Non-fatal
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadNotifications()
    // Poll every 30 seconds
    const interval = setInterval(loadNotifications, 30000)
    return () => clearInterval(interval)
  }, [loadNotifications])

  const navigate = useNavigate()

  const handleNotificationClick = async (notification: CrossOrgNotification) => {
    // Mark as read
    try {
      await api.put(`/api/notifications/${notification.id}/read`)
    } catch { /* non-fatal */ }

    // Switch to that org and navigate
    const result = await selectOrganization(notification.organizationId)
    if (result.success) {
      // Navigate to messages if it's a team message
      const target = notification.type === 'team_message' && notification.data?.senderId
        ? `/messages?contact=${encodeURIComponent(notification.data.senderId)}`
        : '/messages'
      navigate(target)
    }
    setOpen(false)
  }

  // Don't render if no cross-org notifications
  if (!loading && unread === 0 && notifications.length === 0) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9"
          title="Notifications from your other organizations"
        >
          <BellRing className="h-5 w-5 text-amber-500" />
          {unread > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-4 min-w-4 px-1 flex items-center justify-center rounded-full text-[10px] bg-amber-500 text-white border-0"
            >
              {unread > 9 ? '9+' : unread}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0 overflow-hidden"
        sideOffset={8}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b">
          <BellRing className="h-4 w-4 text-amber-500" />
          <div>
            <h3 className="font-semibold text-sm">Other Organizations</h3>
            <p className="text-[11px] text-muted-foreground">Alerts from your other workspaces</p>
          </div>
        </div>

        <div className="max-h-[350px] overflow-y-scroll">
          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-500 mx-auto" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-6 text-center">
              <BellRing className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No cross-org alerts</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map(n => (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Building2 className="h-4 w-4 text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                          {n.organizationName}
                        </span>
                        <ExternalLink className="h-2.5 w-2.5 text-muted-foreground" />
                      </div>
                      <p className="text-sm font-medium truncate">{n.title}</p>
                      {n.body && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{n.body}</p>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
